/**
 * Scout MVP — Ustawienia: dane firmy, styl, limity grafik, motyw, archiwizacja.
 */
import { useEffect, useState } from 'react';
import { API } from '@/utils/api';
import { Settings, Building2, Image, Sun, Moon, Archive, Pencil } from 'lucide-react';

const panel = 'rounded-2xl border border-[var(--sc-border)] bg-[var(--sc-surface)]';

export default function ScoutUstawienia({ client, theme, onToggleTheme, onNavigate, onArchived }) {
  const [usage, setUsage] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(false);

  useEffect(() => {
    let live = true;
    fetch(`${API}/scout/image-usage/${client.id}`)
      .then((r) => r.json())
      .then((d) => { if (live) setUsage(d?.ok ? d.usage : null); })
      .catch(() => { /* noop */ });
    return () => { live = false; };
  }, [client.id]);

  const archive = async () => {
    try {
      const res = await fetch(`${API}/scout/clients/${client.id}`, { method: 'DELETE' });
      const d = await res.json();
      if (d?.ok) onArchived?.();
    } catch { /* noop */ }
  };

  const pct = client.completeness ?? 0;
  return (
    <div className="space-y-5 max-w-2xl" data-testid="view-ustawienia">
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2"><Settings className="w-5 h-5 text-[#FF8A3D]" /> Ustawienia</h2>
        <p className="text-xs text-[var(--sc-t45)] mt-0.5 ml-7">Dane firmy, styl komunikacji, limity i wygląd panelu.</p>
      </div>

      {/* Dane firmy */}
      <div className={`${panel} p-5`} data-testid="settings-company">
        <div className="text-sm font-bold mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-[#FF8A3D]" /> Dane firmy</div>
        <div className="text-sm font-semibold">{client.name}</div>
        <div className="text-xs text-[var(--sc-t45)] mt-0.5">{client.industry || 'branża nieuzupełniona'}{client.city ? ` · ${client.city}` : ''}</div>
        <div className="mt-3 flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-[var(--sc-surface3)] overflow-hidden">
            <div className="h-full rounded-full bg-[#F26A21]" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs font-extrabold text-[#FF8A3D]">{pct}%</span>
        </div>
        <button onClick={() => onNavigate?.('firma')} data-testid="settings-edit-profile-btn"
          className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-orange-400/40 bg-orange-500/10 px-4 py-2 text-xs font-bold text-orange-300 hover:bg-orange-500/20 transition">
          <Pencil className="w-3.5 h-3.5" /> Edytuj w kreatorze „O Twojej firmie"
        </button>
      </div>

      {/* Limit grafik */}
      <div className={`${panel} p-5`} data-testid="settings-image-limit">
        <div className="text-sm font-bold mb-2 flex items-center gap-2"><Image className="w-4 h-4 text-[#FF8A3D]" /> Grafiki od Scouta</div>
        {usage ? (
          <>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full bg-[var(--sc-surface3)] overflow-hidden">
                <div className="h-full rounded-full bg-[#3B82F6]" style={{ width: `${Math.min(100, (usage.used / (usage.limit || 1)) * 100)}%` }} />
              </div>
              <span className="text-xs font-bold text-[var(--sc-t70)]">{usage.used} / {usage.limit} w tym miesiącu</span>
            </div>
            <p className="text-[11px] text-[var(--sc-t40)] mt-2">Limit odnawia się co miesiąc. Grafiki zamawiasz w rozmowie ze Scoutem.</p>
          </>
        ) : (
          <p className="text-xs text-[var(--sc-t40)]">Licznik grafik będzie widoczny po pierwszym użyciu.</p>
        )}
      </div>

      {/* Motyw */}
      <div className={`${panel} p-5 flex items-center gap-4`} data-testid="settings-theme">
        <div className="flex-1">
          <div className="text-sm font-bold flex items-center gap-2">{theme === 'dark' ? <Moon className="w-4 h-4 text-[#FF8A3D]" /> : <Sun className="w-4 h-4 text-[#FF8A3D]" />} Wygląd panelu</div>
          <p className="text-xs text-[var(--sc-t45)] mt-0.5">Obecnie: motyw {theme === 'dark' ? 'ciemny' : 'jasny'}</p>
        </div>
        <button onClick={onToggleTheme} data-testid="settings-theme-toggle"
          className="rounded-xl border border-[var(--sc-border2)] px-4 py-2 text-xs font-bold text-[var(--sc-t60)] hover:bg-[var(--sc-hover)] transition">
          Przełącz na {theme === 'dark' ? 'jasny' : 'ciemny'}
        </button>
      </div>

      {/* Archiwizacja */}
      <div className={`${panel} p-5 flex flex-wrap items-center gap-3`} data-testid="settings-archive">
        <Archive className="w-4 h-4 text-[var(--sc-t40)]" />
        <div className="flex-1 text-xs text-[var(--sc-t45)]">Nie pracujesz już z tą firmą? Zarchiwizuj profil — zniknie z listy, ale dane zostaną.</div>
        {!confirmArchive ? (
          <button onClick={() => setConfirmArchive(true)} data-testid="settings-archive-btn"
            className="rounded-lg border border-[var(--sc-border2)] px-3.5 py-2 text-xs font-semibold text-[var(--sc-t50)] hover:text-red-400 hover:border-red-400/40 transition">
            Archiwizuj firmę
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--sc-t60)]">Na pewno?</span>
            <button onClick={archive} data-testid="settings-archive-confirm" className="rounded-lg bg-red-500/15 border border-red-400/40 px-3 py-1.5 text-xs font-bold text-red-400">Tak, archiwizuj</button>
            <button onClick={() => setConfirmArchive(false)} data-testid="settings-archive-cancel" className="rounded-lg border border-[var(--sc-border2)] px-3 py-1.5 text-xs font-semibold text-[var(--sc-t50)]">Anuluj</button>
          </div>
        )}
      </div>
    </div>
  );
}
